// Port of companion/src/projection.mjs at tag js-core-freeze-2026-09-05 (HEAD cb2d250). V1 is a
// direct port of tmp_nf_main/build_backfill_fingerprints.py (the Python reference); the frozen
// JavaScript is the source of truth for every byte here and the tests pin its outputs. The only
// shape changes are the ones the editor-client lint profile forces (no for...of, no continue, no
// nested ternary); none of them changes an output.
import type {
  Answer,
  Branch,
  CanonFn,
  Fixture,
  HubOption,
  HubSettings,
  Projection,
  ProjectionVersion,
  Step,
  StepCheck,
} from '../../dorian-shared/types/episodeScript';

export function S(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (Array.isArray(value)) return `ARR[${value.map(S).join(',')}]`;
  if (typeof value === 'object') {
    const record = value as Record<string, unknown>;
    return `OBJ{${Object.keys(record).sort().map((key) => `${key}:${S(record[key])}`).join(',')}}`;
  }
  return String(value);
}

export function djb2(value: unknown): number {
  let hash = 5381;
  // Python ord() iterates Unicode code points. The production fixture is BMP-only,
  // but codePointAt keeps this port faithful for astral characters too. Array.from
  // walks a string by code point exactly as the frozen for...of loop does, so a
  // surrogate pair is one character here as well; codePointAt(0) of a whole code
  // point is never undefined.
  Array.from(String(value)).forEach((character) => {
    // eslint-disable-next-line no-bitwise -- the hash is defined in uint32 arithmetic
    hash = (((hash * 33) >>> 0) ^ (character.codePointAt(0) as number)) >>> 0;
  });
  return hash;
}

// Deterministic ordering helper. Deliberately not localeCompare: that is
// ICU/locale dependent, so two machines could disagree on the same fixture.
export function plainCompare(left: string, right: string): number {
  if (left < right) return -1;
  return left > right ? 1 : 0;
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

// Python sorts hubOptions by the tuple (o.get("sortOrder", 0), S(o.get("title"))).
// A missing key is 0; anything present but non-numeric is compared the way Python
// compares the tuple element it actually holds, so fall back to S() on both sides.
// The return is `unknown` on purpose: a document read from the engine is not typed.
function sortOrderOf(option: HubOption): unknown {
  return option.sortOrder === undefined ? 0 : option.sortOrder;
}

export function compareHubOptions(left: HubOption, right: HubOption): number {
  const leftOrder = sortOrderOf(left);
  const rightOrder = sortOrderOf(right);
  const order = isFiniteNumber(leftOrder) && isFiniteNumber(rightOrder)
    ? leftOrder - rightOrder
    : plainCompare(S(leftOrder), S(rightOrder));
  if (order) return order < 0 ? -1 : 1;
  return plainCompare(S(left.title), S(right.title));
}

export function canonBranch(branch: Branch): string {
  const parts = ['B', S(branch.id), '|', S(branch.gotoBranchId), '|'];
  (branch.steps ?? []).forEach((step) => {
    const type = step.stepTypeId;
    if (type === 12) return;
    parts.push(`#${S(type)}~`);
    if (type === 1) {
      parts.push(S(step.text), '|', S(step.characterId), '|', S(step.characterExpressionId));
    } else if (type === 2) {
      parts.push(S(step.text));
    } else if (type === 3) {
      parts.push(
        S(step.text),
        '|',
        S(step.characterId),
        '|',
        S(step.characterExpressionId),
        '|',
        S(step.currencyToggleSettings),
      );
      (step.answers ?? []).forEach((answer) => {
        parts.push(
          '@',
          S(answer.text),
          '|',
          S(answer.answerTypeId),
          '|',
          S(answer.answerPrice),
          '|',
          S(answer.gotoBranchId),
          '|',
          S(answer.eliminationResultType),
          '|',
          S(answer.eliminationResultText),
        );
      });
    } else if (type === 11) {
      const check: StepCheck = step.check ?? {};
      parts.push(S(check.variableId), '|', S(check.operator), '|', S(check.value));
      (step.switch ?? []).forEach((arm) => {
        parts.push('@', S(arm.value), '|', S(arm.gotoBranchId));
      });
    } else if (type === 16) {
      const settings: HubSettings = step.hubSettings ?? {};
      parts.push(S(settings.header?.text), '|', S(settings.subheader?.text));
      const options = [...(step.hubOptions ?? [])].sort(compareHubOptions);
      options.forEach((option) => {
        parts.push(
          '@',
          S(option.title),
          '|',
          S(option.viewType),
          '|',
          S(option.gotoBranchId),
          '|',
          S(option.iconId),
        );
      });
    } else {
      parts.push(S(step.text));
    }
  });
  return parts.join('');
}

function lineFor(branch: Branch, canon: CanonFn): string {
  const stepIds = (branch.steps ?? []).map((step) => S(step.id)).join(',');
  return `${S(branch.id)}:${stepIds}:${djb2(canon(branch))}`;
}

function numericLineSort(left: string, right: string): number {
  const leftId = Number(left.slice(0, left.indexOf(':')));
  const rightId = Number(right.slice(0, right.indexOf(':')));
  if (Number.isFinite(leftId) && Number.isFinite(rightId)) return leftId - rightId;
  return plainCompare(left, right);
}

function project(fixture: Fixture, canon: CanonFn, version: ProjectionVersion): Projection {
  if (!fixture || !Array.isArray(fixture.branches)) {
    throw new TypeError('projection requires a fixture with branches[]');
  }
  const lines = fixture.branches.map((branch) => lineFor(branch, canon));
  lines.sort(numericLineSort);
  return {
    version,
    branchCount: fixture.branches.length,
    digest: [fixture.branches.length, djb2(lines.join(';'))],
    lines,
  };
}

export function projectionV1(fixture: Fixture): Projection {
  return project(fixture, canonBranch, 'v1');
}

// projection.mjs:125-164. Every V2 field is assigned, present or not: S() spells an absent one as
// an empty value, and that is part of the digest.
function answerV2(answer: Answer) {
  return {
    text: answer.text,
    answerTypeId: answer.answerTypeId,
    answerPrice: answer.answerPrice,
    gotoBranchId: answer.gotoBranchId,
    eliminationResultType: answer.eliminationResultType,
    eliminationResultText: answer.eliminationResultText,
    modifiers: answer.modifiers,
    traits: answer.traits,
    requires: answer.requires,
    tags: answer.tags,
    alternatePrices: answer.alternatePrices,
    autoAchievements: answer.autoAchievements,
    data: answer.data,
  };
}

function stepV2(step: Step) {
  return {
    stepTypeId: step.stepTypeId,
    text: step.text,
    characterId: step.characterId,
    characterExpressionId: step.characterExpressionId,
    characterPlacementSide: step.characterPlacementSide,
    subType: step.subType,
    currencyToggleSettings: step.currencyToggleSettings,
    check: step.check,
    switch: step.switch,
    stepAudios: step.stepAudios,
    video: step.video,
    action: step.action,
    roll: step.roll,
    speakerMemoryId: step.speakerMemoryId,
    data: step.data,
    answers: (step.answers ?? []).map(answerV2),
    hubSettings: step.hubSettings,
    hubOptions: step.hubOptions,
  };
}

export function canonBranchV2(branch: Branch): string {
  return S({
    id: branch.id,
    gotoBranchId: branch.gotoBranchId,
    locationId: branch.locationId,
    steps: (branch.steps ?? []).map(stepV2),
  });
}

export function projectionV2(fixture: Fixture): Projection {
  return project(fixture, canonBranchV2, 'v2');
}
